// SelectorImpresora.jsx

import React, { useEffect, useState } from "react";

import impresoraManager, { TIPOS_CONEXION } from "./ImpresoraManager";

/*
 * ================================================================
 * SELECTOR DE IMPRESORAS
 * ================================================================
 *
 * Uso:
 *
 * <SelectorImpresora />
 *
 * También puedes controlar la impresora desde el componente padre:
 *
 * <SelectorImpresora
 *    onSeleccionar={(impresora) => {
 *       console.log(impresora);
 *    }}
 * />
 *
 * ================================================================
 */

export default function SelectorImpresora({ onSeleccionar, onCambioEstado }) {
  /*
   * ==============================================================
   * ESTADOS
   * ==============================================================
   */

  const [impresoras, setImpresoras] = useState([]);

  const [impresoraSeleccionada, setImpresoraSeleccionada] = useState(
    impresoraManager.impresoraActual,
  );

  const [impresoraConectada, setImpresoraConectada] = useState(
    impresoraManager.impresoraConectada,
  );

  const [estado, setEstado] = useState(impresoraManager.estado);

  const [error, setError] = useState(null);

  const [tipoConexion, setTipoConexion] = useState("USB");

  const [anchoPapel, setAnchoPapel] = useState(
    impresoraManager.configuracion?.anchoPapel || 80,
  );

  const [cargando, setCargando] = useState(false);

  const [mostrarGuardadas, setMostrarGuardadas] = useState(true);

  /*
   * ==============================================================
   * ACTUALIZAR IMPRESORAS
   * ==============================================================
   */

  const actualizarImpresoras = () => {
    setImpresoras(impresoraManager.listarImpresoras());

    setImpresoraSeleccionada(impresoraManager.impresoraActual);

    setImpresoraConectada(impresoraManager.impresoraConectada);

    setEstado(impresoraManager.estado);
  };

  /*
   * ==============================================================
   * INICIALIZAR
   * ==============================================================
   */

  useEffect(() => {
    let activo = true;

    const inicializar = async () => {
      try {
        setCargando(true);

        await impresoraManager.inicializar();

        if (!activo) {
          return;
        }

        actualizarImpresoras();
      } catch (err) {
        console.error("Error inicializando impresoras:", err);

        if (activo) {
          setError(err.message || "No fue posible inicializar las impresoras.");
        }
      } finally {
        if (activo) {
          setCargando(false);
        }
      }
    };

    inicializar();

    return () => {
      activo = false;
    };
  }, []);

  /*
   * ==============================================================
   * ESCUCHAR EVENTOS DEL MANAGER
   * ==============================================================
   */

  useEffect(() => {
    const quitarEstado = impresoraManager.on("estado", (nuevoEstado) => {
      setEstado(nuevoEstado.estado);

      setImpresoraConectada(nuevoEstado.impresoraConectada);

      setImpresoraSeleccionada(nuevoEstado.impresoraActual);

      setImpresoras(nuevoEstado.impresoras || []);

      if (typeof onCambioEstado === "function") {
        onCambioEstado(nuevoEstado);
      }
    });

    const quitarConexion = impresoraManager.on("conexion", (impresora) => {
      setImpresoraSeleccionada(impresora);

      setImpresoraConectada(true);

      setEstado("conectada");

      actualizarImpresoras();
    });

    const quitarDesconexion = impresoraManager.on("desconexion", () => {
      setImpresoraConectada(false);

      setEstado("desconectada");

      actualizarImpresoras();
    });

    const quitarImpresora = impresoraManager.on("impresora", (impresora) => {
      setImpresoraSeleccionada(impresora);

      actualizarImpresoras();
    });

    const quitarError = impresoraManager.on("error", (err) => {
      setError(err?.mensaje || err?.message || "Error de impresora.");
    });

    return () => {
      quitarEstado();
      quitarConexion();
      quitarDesconexion();
      quitarImpresora();
      quitarError();
    };
  }, [onCambioEstado]);

  /*
   * ==============================================================
   * CAMBIAR TAMAÑO
   * ==============================================================
   */

  const cambiarTamano = (valor) => {
    const tamano = Number(valor);

    setAnchoPapel(tamano);

    impresoraManager.configurarTamano(tamano);

    /*
     * Si existe una impresora
     * seleccionada, actualizar
     * también su configuración.
     */

    if (impresoraManager.impresoraActual) {
      impresoraManager.impresoraActual.anchoPapel = tamano;

      impresoraManager.impresoraActual.width = tamano === 58 ? 32 : 48;

      /*
       * Guardar configuración.
       */

      impresoraManager.guardarConfiguracion();

      actualizarImpresoras();
    }
  };

  /*
   * ==============================================================
   * CONECTAR NUEVA IMPRESORA
   * ==============================================================
   */

  const conectarNueva = async () => {
    setError(null);

    setCargando(true);

    try {
      /*
       * Configurar tamaño.
       */

      impresoraManager.configurarTamano(anchoPapel);

      /*
       * Configurar tipo.
       */

      impresoraManager.configurar({
        tipoConexion: tipoConexion,

        anchoPapel: anchoPapel,
      });

      /*
       * Abrir selector del navegador.
       */

      const impresora = await impresoraManager.conectar({
        tipoConexion: tipoConexion,
        anchoPapel: anchoPapel,
        forzarNuevo: true,
      });

      setImpresoraSeleccionada(impresora);

      setImpresoraConectada(true);

      setEstado("conectada");

      actualizarImpresoras();

      if (typeof onSeleccionar === "function") {
        onSeleccionar(impresora);
      }
    } catch (err) {
      console.error("Error conectando impresora:", err);

      setError(obtenerMensajeError(err));
    } finally {
      setCargando(false);
    }
  };

  /*
   * ==============================================================
   * SELECCIONAR IMPRESORA GUARDADA
   * ==============================================================
   */

  const seleccionarImpresora = async (impresora) => {
    setError(null);

    setCargando(true);

    try {
      /*
       * Utilizar el tamaño configurado
       * en la impresora.
       */

      const tamano = impresora.anchoPapel || anchoPapel || 80;

      setAnchoPapel(tamano);

      impresoraManager.configurarTamano(tamano);

      /*
       * Seleccionar y conectar.
       */

      const resultado = await impresoraManager.seleccionar(impresora);

      setImpresoraSeleccionada(resultado);

      setImpresoraConectada(true);

      setEstado("conectada");

      actualizarImpresoras();

      if (typeof onSeleccionar === "function") {
        onSeleccionar(resultado);
      }
    } catch (err) {
      console.error("Error seleccionando impresora:", err);

      setImpresoraConectada(false);

      setEstado("error");

      setError(obtenerMensajeError(err));
    } finally {
      setCargando(false);
    }
  };

  /*
   * ==============================================================
   * ELIMINAR IMPRESORA
   * ==============================================================
   */

  const eliminarImpresora = async (event, impresora) => {
    /*
     * Evitar que el click
     * seleccione la impresora.
     */

    event.stopPropagation();

    const confirmar = window.confirm(
      `¿Deseas eliminar "${impresora.nombre}" de las impresoras guardadas?`,
    );

    if (!confirmar) {
      return;
    }

    try {
      if (impresoraManager.impresoraActual?.id === impresora.id) {
        await impresoraManager.desconectar();
      }

      impresoraManager.eliminarImpresora(impresora.id);

      actualizarImpresoras();
    } catch (err) {
      setError(obtenerMensajeError(err));
    }
  };

  /*
   * ==============================================================
   * DESCONECTAR
   * ==============================================================
   */

  const desconectar = async () => {
    setCargando(true);

    try {
      await impresoraManager.desconectar();

      setImpresoraConectada(false);

      setEstado("desconectada");
    } catch (err) {
      setError(obtenerMensajeError(err));
    } finally {
      setCargando(false);
    }
  };

  /*
   * ==============================================================
   * RECONEXIÓN
   * ==============================================================
   */

  const reconectar = async () => {
    setCargando(true);

    setError(null);

    try {
      const resultado = await impresoraManager.reconectar();

      if (!resultado) {
        throw new Error("No fue posible reconectar la impresora.");
      }

      actualizarImpresoras();
    } catch (err) {
      setError(obtenerMensajeError(err));
    } finally {
      setCargando(false);
    }
  };

  /*
   * ==============================================================
   * LIMPIAR ERROR
   * ==============================================================
   */

  const limpiarError = () => {
    setError(null);
  };

  /*
   * ==============================================================
   * RENDER
   * ==============================================================
   */

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 650,
        margin: "0 auto",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* ========================================================
          ENCABEZADO
          ======================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
            }}
          >
            Impresora de tickets
          </h2>

          <div
            style={{
              marginTop: 5,
              color: "#666",
              fontSize: 13,
            }}
          >
            Administración de impresoras
          </div>
        </div>

        <EstadoConexion conectada={impresoraConectada} estado={estado} />
      </div>

      {/* ========================================================
          ERROR
          ======================================================== */}

      {error && (
        <div
          style={{
            background: "#ffe5e5",
            border: "1px solid #ffb3b3",
            borderRadius: 8,
            padding: 12,
            marginBottom: 15,
            color: "#a00000",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span>{error}</span>

          <button
            type="button"
            onClick={limpiarError}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ========================================================
          IMPRESORA ACTUAL
          ======================================================== */}

      {impresoraSeleccionada && (
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: 15,
            marginBottom: 20,
            background: "#fafafa",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#777",
              marginBottom: 5,
            }}
          >
            IMPRESORA ACTUAL
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>{impresoraSeleccionada.nombre}</strong>

              <div
                style={{
                  fontSize: 13,
                  color: "#666",
                  marginTop: 4,
                }}
              >
                {impresoraSeleccionada.tipoConexion} ·{" "}
                {impresoraSeleccionada.anchoPapel || anchoPapel} mm
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
              }}
            >
              {!impresoraConectada && (
                <button type="button" onClick={reconectar} disabled={cargando}>
                  Reconectar
                </button>
              )}

              {impresoraConectada && (
                <button type="button" onClick={desconectar} disabled={cargando}>
                  Desconectar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          TAMAÑO
          ======================================================== */}

      <div
        style={{
          marginBottom: 20,
        }}
      >
        <label
          style={{
            display: "block",
            fontWeight: "bold",
            marginBottom: 8,
          }}
        >
          Tamaño de papel
        </label>

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          <BotonTamano
            valor={58}
            seleccionado={anchoPapel === 58}
            onClick={cambiarTamano}
          />

          <BotonTamano
            valor={80}
            seleccionado={anchoPapel === 80}
            onClick={cambiarTamano}
          />
        </div>
      </div>

      {/* ========================================================
          NUEVA IMPRESORA
          ======================================================== */}

      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 10,
          padding: 15,
          marginBottom: 20,
        }}
      >
        <h3
          style={{
            marginTop: 0,
          }}
        >
          Agregar impresora
        </h3>

        <div
          style={{
            marginBottom: 12,
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: 6,
              fontSize: 13,
              fontWeight: "bold",
            }}
          >
            Tipo de conexión
          </label>

          <select
            value={tipoConexion}
            onChange={(event) => setTipoConexion(event.target.value)}
            disabled={cargando}
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid #ccc",
              borderRadius: 6,
            }}
          >
            <option value={TIPOS_CONEXION.USB}>USB</option>

            <option value={TIPOS_CONEXION.SERIAL}>Puerto Serial / COM</option>

            <option value={TIPOS_CONEXION.BLUETOOTH}>Bluetooth</option>
          </select>
        </div>

        <button
          type="button"
          onClick={conectarNueva}
          disabled={cargando}
          style={{
            width: "100%",
            padding: 12,
            border: 0,
            borderRadius: 6,
            cursor: cargando ? "default" : "pointer",
            background: "#222",
            color: "#fff",
            fontWeight: "bold",
          }}
        >
          {cargando ? "Conectando..." : "Agregar impresora"}
        </button>

        {tipoConexion === TIPOS_CONEXION.BLUETOOTH && (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "#777",
            }}
          >
            La impresora Bluetooth debe ser compatible con Web Bluetooth BLE y
            exponer una característica que permita escritura.
          </div>
        )}
      </div>

      {/* ========================================================
          IMPRESORAS GUARDADAS
          ======================================================== */}

      <div>
        <button
          type="button"
          onClick={() => setMostrarGuardadas(!mostrarGuardadas)}
          style={{
            width: "100%",
            border: 0,
            background: "transparent",
            padding: "10px 0",
            textAlign: "left",
            cursor: "pointer",
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          Impresoras autorizadas ({impresoras.length})
          <span
            style={{
              float: "right",
            }}
          >
            {mostrarGuardadas ? "▲" : "▼"}
          </span>
        </button>

        {mostrarGuardadas && (
          <>
            {impresoras.length === 0 ? (
              <div
                style={{
                  border: "1px dashed #ccc",
                  borderRadius: 8,
                  padding: 20,
                  textAlign: "center",
                  color: "#777",
                }}
              >
                No hay impresoras autorizadas.
                <br />
                <span
                  style={{
                    fontSize: 12,
                  }}
                >
                  Agrega una impresora utilizando el selector de conexión.
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {impresoras.map((impresora) => {
                  const seleccionada =
                    impresoraSeleccionada?.id === impresora.id;

                  const conectada = seleccionada && impresoraConectada;

                  return (
                    <div
                      key={impresora.id}
                      onClick={() => seleccionarImpresora(impresora)}
                      style={{
                        border: seleccionada
                          ? "2px solid #222"
                          : "1px solid #ddd",

                        borderRadius: 10,

                        padding: 14,

                        cursor: cargando ? "default" : "pointer",

                        background: seleccionada ? "#f5f5f5" : "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",

                          justifyContent: "space-between",

                          alignItems: "center",

                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",

                            alignItems: "center",

                            gap: 10,

                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 24,
                            }}
                          >
                            🖨️
                          </span>

                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "bold",

                                overflow: "hidden",

                                textOverflow: "ellipsis",

                                whiteSpace: "nowrap",
                              }}
                            >
                              {impresora.nombre}
                            </div>

                            <div
                              style={{
                                fontSize: 12,

                                color: "#777",

                                marginTop: 3,
                              }}
                            >
                              {impresora.tipoConexion} · {impresora.anchoPapel}{" "}
                              mm
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",

                            alignItems: "center",

                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,

                              color: conectada ? "#168000" : "#888",
                            }}
                          >
                            {conectada ? "● Conectada" : "○ Disponible"}
                          </span>

                          <button
                            type="button"
                            onClick={(event) =>
                              eliminarImpresora(event, impresora)
                            }
                            style={{
                              border: "0",

                              background: "transparent",

                              cursor: "pointer",

                              color: "#b00000",

                              fontSize: 18,
                            }}
                            title="Eliminar impresora"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {seleccionada && (
                        <div
                          style={{
                            marginTop: 10,

                            paddingTop: 10,

                            borderTop: "1px solid #ddd",

                            fontSize: 12,

                            color: "#666",
                          }}
                        >
                          Impresora seleccionada
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ========================================================
          INFORMACIÓN DEL NAVEGADOR
          ======================================================== */}

      <div
        style={{
          marginTop: 20,
          fontSize: 11,
          color: "#888",
          lineHeight: 1.5,
        }}
      >
        USB y Serial requieren un navegador compatible con WebUSB / Web Serial.
        La primera autorización del dispositivo debe realizarse mediante una
        acción del usuario.
      </div>
    </div>
  );
}

/* ================================================================
 * COMPONENTE ESTADO
 * ================================================================ */

function EstadoConexion({ conectada, estado }) {
  let texto = "Desconectada";

  if (estado === "conectando") {
    texto = "Conectando...";
  }

  if (estado === "reconectando") {
    texto = "Reconectando...";
  }

  if (estado === "imprimiendo") {
    texto = "Imprimiendo...";
  }

  if (conectada) {
    texto = estado === "imprimiendo" ? "Imprimiendo..." : "Conectada";
  }

  return (
    <div
      style={{
        padding: "6px 10px",

        borderRadius: 20,

        background: conectada ? "#e5f7e5" : "#f1f1f1",

        color: conectada ? "#168000" : "#777",

        fontSize: 12,

        fontWeight: "bold",
      }}
    >
      ● {texto}
    </div>
  );
}

/* ================================================================
 * BOTÓN DE TAMAÑO
 * ================================================================ */

function BotonTamano({ valor, seleccionado, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(valor)}
      style={{
        flex: 1,

        padding: "12px 10px",

        border: seleccionado ? "2px solid #222" : "1px solid #ccc",

        borderRadius: 8,

        background: seleccionado ? "#f0f0f0" : "#fff",

        cursor: "pointer",

        fontWeight: seleccionado ? "bold" : "normal",
      }}
    >
      {valor} mm
      <div
        style={{
          fontSize: 11,

          color: "#777",

          marginTop: 3,
        }}
      >
        {valor === 58 ? "32 caracteres" : "48 caracteres"}
      </div>
    </button>
  );
}

/* ================================================================
 * MENSAJES DE ERROR
 * ================================================================ */

function obtenerMensajeError(error) {
  if (!error) {
    return "Error desconocido.";
  }

  if (error.name === "NotFoundError") {
    return "No se seleccionó ninguna impresora.";
  }

  if (error.name === "SecurityError") {
    return "El navegador bloqueó el acceso al dispositivo.";
  }

  if (error.name === "NetworkError") {
    return "No fue posible comunicarse con la impresora.";
  }

  if (error.message) {
    return error.message;
  }

  if (error.mensaje) {
    return error.mensaje;
  }

  return String(error);
}
